import React from "react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  LinearProgress,
  SvgIconProps,
  Typography,
  TypographyProps,
} from "@mui/material";
import styled from "@mui/material/styles/styled";

import { Column, Row, StackProps } from "@rui/Stack";

export const Table = styled(Column)(({ theme }) => ({
  borderRadius: "8px",
  border: `1px solid ${theme.palette.shades.neutral[100]}`,

  "& .hide-md, .hide-sm": {
    display: "none",
  },

  [theme.breakpoints.up("sm")]: {
    "& .hide-sm": {
      display: "block",
    },
  },

  [theme.breakpoints.up("md")]: {
    "& .hide-md": {
      display: "flex",
    },
  },
}));

export const TableRow = styled(Accordion)(({ theme }) => ({
  "&::before": {
    display: "none",
  },

  "&:hover": {
    cursor: "default !important",
  },

  "&:nth-of-type(even)": {
    "& .MuiAccordionSummary-root": {
      backgroundColor: "#0000000A",
    },
  },

  "& .topics-table__expand-icon svg": {
    transition: "transform 0.2s ease",
  },

  "& .MuiAccordionSummary-root.Mui-expanded": {
    backgroundColor: theme.palette.shades.ghost[500],
    "& .topics-table__expand-icon svg": {
      transform: "rotate(180deg)",
    },
  },
}));

export const TableRowExpansion = styled(AccordionDetails)(({ theme }) => ({
  padding: 0,
  paddingTop: theme.spacing(2),
}));

export const TableRowExpansionShim = styled(Row)(({ theme }) => ({
  marginTop: theme.spacing(2),
  backgroundColor: theme.palette.shades.ghost[500],
  height: "8px",
  width: "100%",
}));

export const TableRowSummary = styled(AccordionSummary)({
  cursor: "pointer",
  "& .MuiAccordionSummary-content": {
    width: "100%",
  },
});

export const SummaryRow = styled(Row)({
  fontWeight: 500,
  fontSize: "0.75rem",
  "& svg": {
    fontSize: "0.75rem",
  },
});

export const Bullet = styled((props: TypographyProps) => (
  <Typography variant="labelMed" {...props}>
    •
  </Typography>
))(({ theme }) => ({
  color: theme.palette.shades.neutral[200],
}));

type ShareOfMeetingsProps = { value: number };

export const ShareOfMeetings = styled(
  ({ value, ...props }: ShareOfMeetingsProps) => (
    <Column variant="left" spacing={0.5} {...props}>
      <LinearProgress variant="determinate" value={value} />
      <Typography variant="labelMed" sx={{ color: "shades.neutral.500" }}>
        {value}% of meetings
      </Typography>
    </Column>
  )
)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("sm")]: {
    display: "flex",
  },

  "& .MuiLinearProgress-root": {
    borderRadius: "1px",
    height: "4px",
    width: "100px",
    backgroundColor: "#0000001F",
    "& .MuiLinearProgress-bar": {
      backgroundColor: theme.palette.shades.primary[600],
    },
  },
}));

export const CountChip = styled(Typography)(({ theme }) => ({
  padding: "0 6px",
  backgroundColor: "#0000000A",
  fontSize: "0.875rem",
  fontWeight: 500,
  color: theme.palette.shades.neutral[500],
  width: "fit-content",
  borderRadius: "4px",
}));

type TextProps = TypographyProps & { light?: boolean };

export const Text = styled(({ light: _, ...props }: TextProps) => (
  <Typography {...props} />
))(({ light, theme }) => ({
  letterSpacing: "-0.01em",
  fontFamily: theme.typography.fontFamilySecondary,
  fontSize: "0.875rem",
  lineHeight: 17 / 14,
  color: light
    ? theme.palette.shades.neutral[400]
    : theme.palette.shades.neutral[600],
}));

export const BulletedList = styled("ul")({
  padding: 0,
  margin: 0,
});

export const BulletedListItem = styled("li")(({ theme }) => ({
  marginLeft: "24px",
  letterSpacing: "-0.01em",
  fontFamily: theme.typography.fontFamilySecondary,
  color: theme.palette.shades.neutral[600],
  fontSize: "0.875rem",
  padding: "2px 4px",
  borderRadius: "4px",
  width: "fit-content",
}));

interface ChipProps extends StackProps {
  Icon: React.JSXElementConstructor<SvgIconProps>;
  type: "action_items" | "clips" | "key_questions";
}

export const Chip = styled(
  ({ Icon, children, type: _, ...props }: ChipProps) => (
    <Row {...props} variant="center" spacing={0.5}>
      <Icon />
      <span>{children}</span>
    </Row>
  )
)(({ theme, type }) => ({
  color:
    type === "action_items"
      ? theme.palette.shades.primary[400]
      : type === "key_questions"
        ? theme.palette.chart.orange.dark
        : "#80B22D",
  padding: "2px 4px",
  fontSize: "0.75rem",
  fontWeight: 500,
  borderRadius: "2px",
  backgroundColor:
    type === "action_items"
      ? theme.palette.shades.primary[50]
      : type === "key_questions"
        ? theme.palette.shades.orange[50]
        : "#E1F2C4",
  whiteSpace: "nowrap",
  "& .MuiSvgIcon-root": {
    fontSize: "inherit",
    color: "inherit",
  },
}));

export const TableSubHeading = styled(Row)(({ theme }) => ({
  paddingBottom: theme.spacing(0.75),
  borderBottom: "1px solid",
  borderColor: theme.palette.shades.neutral[100],
  width: "100%",
  minHeight: "28px",
}));
